import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import ActionPathField from './ActionPathField';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import {
  appendFileNameToFolderPath,
  getPathFileName,
  normalizePathValue,
} from '../../Utils/pathUtils';

const cardTitleTypographyProps = {
  sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 },
};

const ActionsAddPasswordDocument = ({
  actionType,
  allowReplaceExistingDocument = false,
  caseid,
  defaultTargetPath,
  documentType,
  extension,
  initialScope = 'event',
  onSaveStatusChange,
  settingId,
  tags,
  titleFallback,
  titleKey,
}) => {
  const { t } = useTranslation();
  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState(defaultTargetPath);
  const [documentPassword, setDocumentPassword] = useState('');
  const [replaceExistingDocument, setReplaceExistingDocument] = useState(false);
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [replaceWarningOpen, setReplaceWarningOpen] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    handleBrowseDirectory: handleBrowseSourceFile,
    FolderBrowserComponent: SourceFileBrowserComponent,
  } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const {
    handleBrowseDirectory: handleBrowseTargetFolder,
    FolderBrowserComponent: TargetFolderBrowserComponent,
  } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const defaultFileName = `{filename}_protected.${extension}`;
        const currentFileName = getPathFileName(targetPath, defaultFileName);
        setTargetPath(appendFileNameToFolderPath(folderPath, currentFileName));
      }
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      normalizePathValue(sourcePath) === normalizePathValue(initial.sourceFilePath)
      && ((allowReplaceExistingDocument && replaceExistingDocument)
        || normalizePathValue(targetPath) === normalizePathValue(initial.targetPath))
      && documentPassword === (initial.documentPassword || '')
      && (!allowReplaceExistingDocument
        || replaceExistingDocument === Boolean(initial.replaceExistingDocument))
    ),
    customSaveData: () => ({
      sourceFilePath: normalizePathValue(sourcePath),
      targetPath: normalizePathValue(targetPath),
      documentPassword,
      ...(allowReplaceExistingDocument && { replaceExistingDocument }),
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setSourcePath(customData.sourceFilePath || '');
      const storedTargetPath = normalizePathValue(customData.targetPath);
      setTargetPath(storedTargetPath || defaultTargetPath);
      setDocumentPassword(customData.documentPassword || '');
      setReplaceExistingDocument(
        allowReplaceExistingDocument && Boolean(customData.replaceExistingDocument),
      );
    },
  });

  const sourceMissing = !sourcePath.trim();
  const targetMissing = !replaceExistingDocument && !targetPath.trim();
  const passwordMissing = !documentPassword.trim();
  const hasValidationError = sourceMissing || targetMissing || passwordMissing;

  const handleBrowseSourceClick = useCallback(() => {
    setSourceBrowseDisabled(true);
    handleBrowseSourceFile();
  }, [handleBrowseSourceFile]);

  const handleBrowseTargetClick = useCallback(() => {
    setTargetBrowseDisabled(true);
    handleBrowseTargetFolder();
  }, [handleBrowseTargetFolder]);

  const baseHandleSave = actionSettings.handleSave;
  const handleSave = useCallback(() => {
    if (hasValidationError) {
      return;
    }
    if (allowReplaceExistingDocument && replaceExistingDocument) {
      setReplaceWarningOpen(true);
      return;
    }
    baseHandleSave();
  }, [allowReplaceExistingDocument, baseHandleSave, hasValidationError, replaceExistingDocument]);

  const handleConfirmReplaceSave = useCallback(() => {
    setReplaceWarningOpen(false);
    baseHandleSave();
  }, [baseHandleSave]);

  const sourcePlaceholder = useMemo(
    () => `C:\\Input\\document.${extension}`,
    [extension],
  );

  return (
    <>
      <ActionSettingsLayout
        isEnabled={actionSettings.isEnabled}
        onEnabledChange={actionSettings.setIsEnabled}
        title={t(titleKey, titleFallback)}
        isSaveDisabled={actionSettings.isSaveDisabled}
        hasValidationError={hasValidationError}
        onSave={handleSave}
        onForceRun={actionSettings.handleForceRun}
        forceRunLoading={actionSettings.forceRunLoading}
        conditions={actionSettings.conditions}
        onConditionsChange={actionSettings.handleConditionsChange}
        tags={tags}
        popUpOpen={actionSettings.popUpOpen}
        forceRunResult={actionSettings.forceRunResult}
        onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      >
        <Grid item xs={12}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title={t('workflow.add_password_source_title', 'Source file')}
              titleTypographyProps={cardTitleTypographyProps}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <ActionPathField
                browseDisabled={sourceBrowseDisabled}
                browseLabel={t('workflow.add_password_source_browse', 'Browse file')}
                caseid={caseid}
                error={sourceMissing}
                helperText={sourceMissing
                  ? t('workflow.add_password_source_required', 'Source file path is required.')
                  : t(
                    'workflow.add_password_source_help',
                    'Select the document that will receive password protection.',
                  )}
                label={t(
                  'workflow.add_password_source_label',
                  `Source ${documentType} file`,
                )}
                onBrowse={handleBrowseSourceClick}
                onChange={setSourcePath}
                placeholder={sourcePlaceholder}
                tags={tags}
                value={sourcePath}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title={t('workflow.add_password_target_title', 'Protected file destination')}
              titleTypographyProps={cardTitleTypographyProps}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <ActionPathField
                browseDisabled={targetBrowseDisabled}
                browseLabel={t('workflow.add_password_target_browse', 'Browse folder')}
                caseid={caseid}
                disabled={allowReplaceExistingDocument && replaceExistingDocument}
                error={targetMissing}
                helperText={targetMissing
                  ? t('workflow.add_password_target_required', 'Destination path is required.')
                  : t(
                    'workflow.add_password_target_help',
                    'Choose the full path for the protected document. Dynamic tags are supported.',
                  )}
                label={t('workflow.add_password_target_label', 'Destination path')}
                onBrowse={handleBrowseTargetClick}
                onChange={setTargetPath}
                placeholder={defaultTargetPath}
                tags={tags}
                value={targetPath}
              />

              {allowReplaceExistingDocument && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={replaceExistingDocument}
                        onChange={(event) => setReplaceExistingDocument(event.target.checked)}
                      />
                    )}
                    label={t(
                      'workflow.replace_existing_document',
                      'Replace the existing document',
                    )}
                  />
                  <FormHelperText>
                    {t(
                      'workflow.replace_existing_document_help',
                      'When enabled, the source file will be overwritten and the destination path is ignored.',
                    )}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={t('workflow.add_password_password_title', 'Document password')}
              titleTypographyProps={cardTitleTypographyProps}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {t('workflow.add_password_password_label', 'Password')}
              </Typography>
              <TagEditor
                caseid={caseid}
                value={documentPassword}
                onChange={setDocumentPassword}
                placeholder={t(
                  'workflow.add_password_password_placeholder',
                  'Enter a password. Dynamic tags are supported.',
                )}
                singleLine
                tags={tags}
              />
              <FormHelperText error={passwordMissing}>
                {passwordMissing
                  ? t('workflow.add_password_password_required', 'Document password is required.')
                  : t(
                    'workflow.add_password_password_help',
                    'Tags can be used to generate a different password for each document.',
                  )}
              </FormHelperText>
            </CardContent>
          </Card>
        </Grid>
      </ActionSettingsLayout>

      {SourceFileBrowserComponent}
      {TargetFolderBrowserComponent}

      <Dialog
        open={replaceWarningOpen}
        onClose={() => setReplaceWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('workflow.add_password_replace_warning_title', 'Replace the Source Document?')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(
              'workflow.add_password_replace_warning_body',
              'The protected document will overwrite the selected source file.',
            )}
          </Alert>
          <DialogContentText sx={{ mt: 2 }}>
            {t(
              'workflow.add_password_replace_warning_confirm',
              'Do you want to save with this option enabled?',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReplaceWarningOpen(false)}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button color="warning" variant="contained" onClick={handleConfirmReplaceSave}>
            {t('workflow.add_password_replace_warning_save', 'Save Anyway')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ActionsAddPasswordDocument);
